function getIncompatibleMessage(sourceType, destType) {
  return `${ destType } is not compatible with ${ sourceType }`;
}

function getInvalidDataTypeMessage(destType) {
  return `${ destType } is not a valid Data Type`;
}

function getInvalidVarcharFormatMessage(limit) {
  const lengthMessage = `, where 'length' should be equal to or greater than ${ limit }`;

  return `Please ensure the correct varchar format: varchar(length)${ limit >= 0 ? lengthMessage : '' }`
}

function getInvalidDecimalFormatMessage(precision, scope) {
  const paramsMessage = `, where 'precision' should be equal to or greater than ${ precision }, and 'scale' should be equal to or greater than ${ scope }.`

  return `Please ensure the correct decimal format: decimal(precision, scale)${ precision >= 0 ? paramsMessage : '' }`
}

export function validateDataType(sourceType: string, destType: any, allowedTypes: any[]) {
  if (destType != null && typeof destType === 'object') {
    return null;
  }

  if (!destType) {
    return null;
  }

  const incompatibleMessage = getIncompatibleMessage(sourceType, destType);
  const invalidMessage = getInvalidDataTypeMessage(destType);

  const type = String(destType).replace(/ /g, '');
  sourceType = sourceType.replace(/ /g, '');

  if (type.includes('varchar')) {
    const varcharFormat: RegExp = /^varchar(\s)*\((\s)*([0-9])+(\s)*\)(\s)*$/;

    if (!isTypeInOptions('varchar', allowedTypes)) {
      return {
        dataType: incompatibleMessage
      };
    }

    const sourceLimit = getVarcharCharacterLimit(sourceType);

    if (!varcharFormat.test(String(destType))) {
      return {
        dataType: getInvalidVarcharFormatMessage(sourceLimit)
      };
    }

    if (!validateVarcharLimit(type, sourceType)) {
      return {
        dataType: getInvalidVarcharFormatMessage(sourceLimit)
      };
    }

    return null;
  }

  if (type.includes('decimal')) {
    const decimalFormat: RegExp = /^decimal(\s)*\((\s)*([0-9])+(\s)*\,(\s)*([0-9])+(\s)*\)(\s)*$/;

    if (!isTypeInOptions('decimal', allowedTypes)) {
      return {
        dataType: incompatibleMessage
      };
    }

    const sourcePrecision = getDecimalPrecision(sourceType);
    const sourceScope = getDecimalScope(sourceType);

    if (!decimalFormat.test(String(destType))) {
      return {
        dataType: getInvalidDecimalFormatMessage(sourcePrecision, sourceScope)
      };
    }

    if (!validateDecimalLimits(type, sourceType)) {
      return {
        dataType: getInvalidDecimalFormatMessage(sourcePrecision, sourceScope)
      };
    }

    return null;
  }

  const dataTypeFormat: RegExp = /^(\s)*[a-z]*[A-Z]*(\s)*$/;
  if (!dataTypeFormat.test(String(destType))) {
    return {
      dataType: invalidMessage
    };
  }

  if (!isTypeInOptions(type, allowedTypes, true)) {
    return {
      dataType: incompatibleMessage
    };
  }

  return null;
}

function isTypeInOptions(type: string, allowedTypes: any[], fullMatch = false): boolean {
  for (let typeIndex = 0; typeIndex < allowedTypes.length; typeIndex++) {
    if (fullMatch && String(allowedTypes[typeIndex].type_display).toLowerCase() === type.toLowerCase()) {
      return true;
    }

    if (String(allowedTypes[typeIndex].type_display).includes(type)) {
      return true;
    }
  }

  return false;
}

function validateVarcharLimit(type: any, sourceType): boolean {
  const sourceLimit = getVarcharCharacterLimit(sourceType);
  const destLimit = getVarcharCharacterLimit(type);

  if (destLimit === -1) {
    return false;
  }

  return destLimit >= sourceLimit;
}

function getVarcharCharacterLimit(type: any): Number {
  let limit = type.split('(');
  if (!validateSplit(limit)) {
    return -1;
  }

  limit = limit[1].split(')');
  if (!validateSplit(limit)) {
    return -1;
  }

  return Number(limit[0]);
}

function validateDecimalLimits(type: any, sourceType: any): boolean {
  const sourcePrecision = getDecimalPrecision(sourceType);
  const sourceScope = getDecimalScope(sourceType);
  const destPrecision = getDecimalPrecision(type);
  const destScope = getDecimalScope(type);

  if (destPrecision === -1 || destScope === -1) {
    return false;
  }

  if (destPrecision < sourcePrecision) {
    return false;
  }

  return destScope >= sourceScope;
}

function getDecimalPrecision(type: any): Number {
  let precision = type.split('(');
  if (!validateSplit(precision)) {
    return -1;
  }

  precision = precision[1].split(',');
  if (!validateSplit(precision)) {
    return -1;
  }

  return Number(precision[0]);
}

function getDecimalScope(type: any): Number {
  let scope = type.split(')');
  if (!validateSplit(scope)) {
    return -1;
  }

  scope = scope[0].split(',');
  if (!validateSplit(scope)) {
    return -1;
  }

  return Number(scope[1]);
}

function validateSplit(split: any): boolean {
  return split.length !== 1;
}
